import {
  Zap,
  Settings,
  Users,
  Clock,
  TrendingUp,
  Lightbulb,
  Target,
  Puzzle,
  MessageSquare,
  Linkedin,
  Mail,
  Layers,
  Compass,
  HeartHandshake,
  BarChart3,
  Rocket,
  Workflow,
  UserCheck,
  Brain,
} from "lucide-react";

export const siteMeta = {
  title: "Business Growth & Technology Consultant for SMEs | Teqie",
  description:
    "Practical business consulting for founders and growing SMEs. Improve operations, processes, technology, AI adoption and project delivery through diagnostic-led consulting, implementation and coaching.",
};

export const contactInfo = {
  bookingUrl: "https://calendar.app.google/9En7fv86tps8Rfw2A",
  whatsapp: "https://api.whatsapp.com/send?phone=447599208763",
  linkedin: "https://www.linkedin.com/in/shakirahakinwale",
  email: "hello@teqie.co.uk",
};

export const hero = {
  badge: "For Founders, SMEs & Growing Businesses",
  headlineLines: ["Make your business", "easier to run and", "better equipped to grow."],
  accentWords: ["easier to run", "grow"],
  subheadline:
    "I help founders and growing businesses find what's slowing them down, identify opportunities they're missing, and turn them into practical improvements across operations, technology, projects, people and strategy.",
  extra: "You don't need to know what the solution is before we talk. That's part of the process.",
  primaryCta: "Book a Growth Conversation",
  secondaryCta: "See Where I Can Help",
  founderName: "Shakirah Akinwale",
  founderTitle: "Founder & Principal Consultant, Teqie",
};

export const marqueeTags = [
  "Business Analysis",
  "Project & Programme Delivery",
  "Digital Transformation",
  "Operations",
  "Technology Strategy",
  "AI & Automation",
  "Product & Service Design",
  "Coaching & Mentoring",
  "Entrepreneurship",
  "Innovation",
];

export const recognition = {
  heading: "Does any of this sound familiar?",
  intro:
    "Most clients don't start by asking for \"digital transformation\" or \"process optimisation\". They start with a business that has become harder to run than it should be.",
  items: [
    {
      title: "Everything still depends on you",
      description:
        "The business is growing, but decisions, approvals and important knowledge still sit with the founder. Taking a day off feels risky.",
    },
    {
      title: "Too much work is manual",
      description:
        "Your team spends hours copying information, chasing updates, producing reports or repeating tasks that could be simplified or automated.",
    },
    {
      title: "Your tools don't work together",
      description:
        "You have plenty of software, but information is scattered between spreadsheets, emails, WhatsApp, CRMs, project tools and people's heads.",
    },
    {
      title: "Growth is creating more complexity",
      description:
        "Processes that worked when you were smaller are starting to break as customers, projects, staff or transactions increase.",
    },
    {
      title: "Projects keep drifting",
      description:
        "Important ideas and improvement projects are discussed repeatedly but struggle to move from intention to delivery.",
    },
    {
      title: "You know AI could help — somewhere",
      description:
        "You can see the potential of AI and automation but aren't sure where it would genuinely save time, improve service or generate a return.",
    },
    {
      title: "Customer experience feels inconsistent",
      description:
        "Enquiries are missed, follow-ups vary, onboarding is clunky or customers have to chase for information.",
    },
    {
      title: "You can't see what's happening clearly",
      description:
        "Important information exists, but you don't have simple reporting that tells you what is working, what needs attention and what decisions to make.",
    },
    {
      title: "Your team is capable but stretched",
      description:
        "People are constantly busy, but too much time is being consumed by administration, unclear responsibilities, avoidable meetings or inefficient processes.",
    },
    {
      title: "You have an opportunity but need help executing it",
      description:
        "You know what you would like to improve, launch or build — but need someone who can help turn the idea into a practical plan and get it delivered.",
    },
  ],
  closing:
    "If you recognise the symptoms but aren't sure what's causing them, that's fine. Diagnosing the real problem is where I start.",
};

export const routes = {
  heading: "Start wherever you are",
  cards: [
    {
      title: "I know what needs fixing",
      copy:
        "You already have a clear problem, project or bottleneck and need someone who can get underneath it quickly, recommend the right approach and help make the change happen.",
      examples: [
        "Fix a broken process",
        "Improve a CRM or workflow",
        "Automate repetitive administration",
        "Rescue or structure a project",
        "Improve customer onboarding",
        "Introduce useful AI",
        "Improve reporting",
        "Review your technology stack",
        "Redesign an internal process",
      ],
      cta: "Talk Through the Problem",
      action: "booking",
    },
    {
      title: "Something needs to improve — but I'm not sure what",
      copy:
        "You know the business could work better, but the symptoms are spread across people, systems, processes and day-to-day operations. I'll look at how the business works today, identify where time, money, capacity or opportunities are being lost, and help you decide what changes would create the biggest return.",
      examples: [],
      cta: "Start with a Growth Diagnostic",
      action: "diagnostic",
    },
  ],
};

export const diagnostic = {
  heading: "The Teqie Growth & Efficiency Diagnostic",
  subheading: "Before prescribing solutions, understand what's really happening.",
  copy:
    "Growing businesses often accumulate friction gradually. A workaround becomes a process. A spreadsheet becomes mission-critical. One person's knowledge becomes a dependency. New software gets added without removing the old way of working. The Growth & Efficiency Diagnostic creates a structured view of where your business is today, where the greatest constraints and opportunities lie, and what to prioritise next.",
  areas: [
    {
      icon: TrendingUp,
      title: "Growth & Commercial",
      items: ["Where revenue opportunities may be leaking", "Pricing, propositions or commercial processes", "Sales and lead management", "Capacity constraints that restrict growth"],
    },
    {
      icon: HeartHandshake,
      title: "Customer Experience",
      items: ["Enquiry handling and onboarding", "Communication and service delivery", "Customer friction", "Retention opportunities"],
    },
    {
      icon: Workflow,
      title: "Operations & Processes",
      items: ["Repetitive work and duplication", "Bottlenecks and hand-offs", "Unclear processes", "Founder dependencies"],
    },
    {
      icon: Brain,
      title: "Technology, Data & AI",
      items: ["Underused software and disconnected systems", "Automation opportunities and useful applications of AI", "Reporting and data quality", "Technology spend and ROI"],
    },
    {
      icon: Users,
      title: "People & Capacity",
      items: ["Unclear responsibilities and communication gaps", "Workload and delegation", "Capability", "Adoption of new ways of working"],
    },
    {
      icon: Compass,
      title: "Strategy & Delivery",
      items: ["Priority overload and stalled projects", "Execution gaps", "Decision-making", "Programme and project structure"],
    },
  ],
  outputHeading: "What you receive",
  outputIntro: "A practical improvement roadmap identifying:",
  outputs: [
    "Immediate quick wins",
    "Highest-value opportunities",
    "Areas of unnecessary cost or effort",
    "Risks and dependencies",
    "Recommended projects",
    "Technology or automation opportunities",
    "Leadership or capability development needs",
    "Suggested priorities for the next 30, 60 and 90 days",
  ],
  note:
    "Sometimes the answer is a technology project. Sometimes it's a process change, clearer ownership, better information, coaching, or simply stopping work that no longer adds value.",
  cta: "Book a Growth Conversation",
};

export const improveAreas = {
  heading: "What could I help you improve?",
  intro: "Organised around outcomes — not consulting disciplines.",
  items: [
    {
      icon: Clock,
      title: "Save time and reduce unnecessary work",
      examples: ["Automate repetitive administration", "Simplify workflows", "Reduce duplicate data entry", "Improve reporting", "Remove unnecessary steps or meetings"],
    },
    {
      icon: Rocket,
      title: "Increase capacity for growth",
      examples: ["Redesign processes that don't scale", "Reduce founder dependency", "Improve delegation", "Create clearer operating systems", "Prepare the business for more customers or staff"],
    },
    {
      icon: HeartHandshake,
      title: "Improve customer experience",
      examples: ["Redesign enquiries and onboarding", "Improve communication", "Reduce missed follow-ups", "Improve service hand-offs", "Streamline support"],
    },
    {
      icon: Settings,
      title: "Get more value from technology",
      examples: ["Improve existing tools before buying new ones", "Connect systems", "Review your technology stack", "Introduce automation", "Identify useful AI opportunities", "Improve data visibility"],
    },
    {
      icon: Target,
      title: "Get important projects delivered",
      examples: ["Turn ideas into structured projects", "Define outcomes and responsibilities", "Coordinate suppliers and stakeholders", "Unblock stalled initiatives", "Manage implementation and adoption"],
    },
    {
      icon: BarChart3,
      title: "Make better business decisions",
      examples: ["Identify priorities", "Create useful management information", "Challenge assumptions", "Evaluate opportunities", "Provide an experienced external perspective"],
    },
    {
      icon: UserCheck,
      title: "Develop the founder or leadership team",
      examples: ["Strategic thinking", "Confidence in decision-making", "Delegation", "Managing change", "Accountability", "Developing leadership capability"],
    },
  ],
};

export const useCases = {
  heading: "What does this look like in real life?",
  intro:
    "Common scenarios — described in plain English. These are typical situations, kept separate from verified client case studies below.",
  items: [
    {
      quote: "We're spending hours chasing customers.",
      response:
        "Review the current process, identify where information or responsibility is being lost, then simplify communications and automate appropriate reminders and follow-ups.",
    },
    {
      quote: "We're paying for software we barely use.",
      response:
        "Audit the current technology stack, identify duplicated or underused tools, improve configuration and remove unnecessary subscriptions before recommending anything new.",
    },
    {
      quote: "Everything comes through me.",
      response:
        "Identify where the founder has become the bottleneck, clarify decision rights, document key processes and introduce tools or routines that allow the team to operate more independently.",
    },
    {
      quote: "Our CRM is basically an expensive address book.",
      response:
        "Redesign how leads and customers move through the business, clean up the data and configure the CRM around the actual sales and service process.",
    },
    {
      quote: "I want to use AI, but I don't know where to start.",
      response:
        "Map repetitive, information-heavy or decision-support tasks and identify a small number of safe, useful AI applications with a clear business case.",
    },
    {
      quote: "Our team keeps doing the same admin.",
      response:
        "Map the workflow, remove unnecessary steps and automate repetitive tasks where appropriate.",
    },
    {
      quote: "We've outgrown spreadsheets.",
      response:
        "Understand what the spreadsheets are actually doing for the business before selecting or designing a more scalable solution.",
    },
    {
      quote: "Our customer journey is messy.",
      response:
        "Map the experience from first enquiry through onboarding and delivery, identify friction and redesign the process around the customer.",
    },
    {
      quote: "We've been talking about this project for months.",
      response:
        "Turn the idea into a defined project with outcomes, scope, responsibilities, milestones and an achievable delivery plan — then support or lead implementation.",
    },
    {
      quote: "We're growing, but it feels chaotic.",
      response:
        "Review how work, information and decisions move through the business and create a practical operating structure that can support the next stage of growth.",
    },
    {
      quote: "I need an experienced person to think this through with me.",
      response:
        "Provide ongoing advisory, coaching or mentoring to help the founder evaluate opportunities, solve problems and make better decisions.",
    },
    {
      quote: "I have an idea for a digital product.",
      response:
        "Help validate the need, understand users, define requirements, structure delivery, coordinate development and test whether the product works for the people expected to use it.",
    },
  ],
};

export const processSteps = {
  heading: "From business friction to measurable improvement",
  steps: [
    {
      step: "01",
      title: "Understand",
      tagline: "I start with the business, not the technology.",
      description:
        "Understand the problem, goals, people, current processes, customer experience and constraints.",
    },
    {
      step: "02",
      title: "Diagnose",
      tagline: "Find the real cause, not just the visible symptom.",
      description:
        "Map what is happening today and identify where effort, money, capacity or opportunities are being lost.",
    },
    {
      step: "03",
      title: "Prioritise",
      tagline: "Focus on what will create the greatest return.",
      description:
        "Separate quick wins from larger changes and agree what should happen now, later — or not at all.",
    },
    {
      step: "04",
      title: "Improve",
      tagline: "Turn recommendations into working changes.",
      description:
        "This may involve process redesign, project delivery, technology, automation, AI, data, facilitation, supplier coordination, training or hands-on implementation.",
    },
    {
      step: "05",
      title: "Embed",
      tagline: "Make sure the improvement lasts.",
      description:
        "Document the change, support adoption, coach the people involved and measure whether the intended result has actually been achieved.",
    },
  ],
};

export const engagements = {
  heading: "Different problems need different levels of support",
  intro: "Typical engagement formats — not rigid products. You don't need to choose before we speak.",
  items: [
    {
      icon: Zap,
      title: "Focused Improvement Sprint",
      subtitle: "Solve a clear problem quickly.",
      copy:
        "For a specific process, technology, operational or project issue that can be diagnosed and moved forward in a short, focused engagement.",
      uses: ["Process review", "Workflow redesign", "Technology troubleshooting", "Automation opportunity", "CRM improvement", "Project reset", "Decision workshop"],
      usesLabel: "Typical uses",
      cta: "Discuss a Sprint",
      accent: "#0e8d86",
    },
    {
      icon: Layers,
      title: "Business Growth Programme",
      subtitle: "Fix the constraints that are holding the business back.",
      copy:
        "A structured improvement programme for businesses that need changes across more than one area. I diagnose the current position, agree priorities with you, then we work through the highest-value improvements together.",
      uses: ["Operating processes", "Technology and data", "Customer journey", "AI and automation", "Project delivery", "Roles and responsibilities", "Management information", "Adoption and training"],
      usesLabel: "Potential work may include",
      cta: "Explore a Growth Programme",
      accent: "#A86532",
    },
    {
      icon: Users,
      title: "Ongoing Advisor & Coach",
      subtitle: "An experienced thinking partner as the business evolves.",
      copy:
        "Flexible ongoing support for founders or leaders who want a combination of strategic advice, practical problem-solving, accountability, mentoring and coaching.",
      uses: ["The founder is navigating growth", "The business doesn't yet need a full-time COO/CTO", "Several improvement projects are underway", "Difficult decisions need an objective perspective", "Leadership or delegation needs strengthening", "Priorities change frequently"],
      usesLabel: "Useful when",
      cta: "Talk About Ongoing Support",
      accent: "#2D6A4F",
    },
  ],
  note:
    "The right format is agreed after the initial conversation. You do not need to decide which of these you need before contacting Teqie.",
};

export const caseStudies = {
  heading: "Real work, real results",
  intro: "Verified client engagements, told as they happened. No invented numbers.",
  items: [
    {
      sector: "Property / Operations",
      title: "Reducing hours of manual payment chasing",
      situation:
        "A property management business was tracking payments and follow-ups across email, WhatsApp and spreadsheets. Staff were spending significant time manually checking records and chasing updates.",
      found:
        "The information required to manage the process already existed, but it was fragmented across multiple tools and there was no consistent workflow for follow-up.",
      changed:
        "The process was redesigned around a central source of information, with clearer tracking and automated communication for appropriate stages of the journey.",
      result:
        "Hours of routine administration were removed from the process each month, while improving the consistency and visibility of customer follow-up.",
    },
    {
      sector: "Coaching / Professional Services",
      title: "Creating capacity by redesigning client onboarding",
      situation:
        "A growing coaching business was successfully attracting clients, but onboarding and administration were consuming increasing amounts of founder time.",
      found:
        "Several parts of the client journey depended on repetitive manual activity and information was spread across different tools.",
      changed:
        "The back-office workflow was redesigned and tools were connected to create a simpler client-management and onboarding process.",
      result:
        "Administrative time associated with the process was reduced significantly — creating additional capacity to serve clients and focus on growth.",
    },
    {
      sector: "E-commerce / Founder Advisory",
      title: "Growing the business without growing the founder's workload",
      situation:
        "An e-commerce founder had a growing business but was increasingly stretched across operations, pricing, technology and day-to-day decisions.",
      found:
        "Growth was creating operational complexity and too many business decisions still relied on the founder.",
      changed:
        "Through an ongoing advisory relationship, work included operational improvement, pricing, technology decisions and strategic problem-solving.",
      result:
        "The business increased its operating capacity while reducing the amount of day-to-day work requiring direct founder involvement.",
    },
  ],
};

export const founder = {
  heading: "Business thinking. Technology understanding. Hands-on delivery.",
  name: "Shakirah Akinwale",
  role: "Founder & Principal Consultant, Teqie",
  photo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_teqie-new/artifacts/aem4d1vu_Shakirah_Akinwale_Profile_Photo_High_Resolution.jpg",
  paragraphs: [
    "I'm Shakirah Akinwale, founder of Teqie. My work sits at the intersection of business, technology, operations and delivery. I've worked across project management, business analysis, digital transformation, innovation, product development and entrepreneurship — helping organisations move from ideas and problems to practical implementation.",
    "That breadth matters because business problems rarely sit neatly inside one discipline. A technology problem may actually be a process problem. A capacity problem may be caused by poor delegation. A growth problem may be created by onboarding, data or customer experience.",
    "My role is to understand the whole picture, challenge assumptions and help identify the most useful intervention rather than defaulting to a particular tool or technology.",
    "I also bring coaching and mentoring into my work, helping founders and leaders develop the confidence, judgement and capability to keep improving after an engagement ends.",
  ],
  qualification: "CMI Level 7 Executive Coaching & Mentoring",
  transparency:
    "Teqie is my consultancy and delivery practice. I work directly with clients and, where a project needs additional specialist expertise, bring in trusted collaborators when appropriate.",
  tags: marqueeTags,
  linkedin: contactInfo.linkedin,
};

export const coaching = {
  heading: "Sometimes the business needs fixing. Sometimes the leader needs space to think. Often it's both.",
  copy:
    "Consulting gives you expertise. Delivery turns the idea into action. Coaching and mentoring help you develop your own ability to navigate what comes next. Depending on the engagement, I may work with you as a consultant, delivery partner, coach, mentor — or a combination of these.",
  columns: [
    {
      icon: Lightbulb,
      title: "Consult",
      description: "Understand the problem, challenge assumptions and decide what should change.",
    },
    {
      icon: Puzzle,
      title: "Deliver",
      description: "Turn recommendations into practical improvements, projects, systems and working processes.",
    },
    {
      icon: UserCheck,
      title: "Coach",
      description: "Build your confidence, decision-making and capability so improvement doesn't remain dependent on an external consultant.",
    },
  ],
  closing: "Most business growth programmes can blend all three where useful.",
};

export const finalCta = {
  heading: "You don't need to know what the solution is.",
  body:
    "If something in the business feels harder, slower or more expensive than it should — or you know there is an opportunity you're not fully capturing — let's start there. The first conversation is about understanding what's happening, what you want to achieve and whether Teqie is the right fit to help.",
  primaryCta: "Book a Growth Conversation",
  secondaryCta: "Message on WhatsApp",
};

export const footerData = {
  brand: "Teqie",
  tagline: "Helping businesses work better, grow smarter and make better use of technology.",
  links: [
    {
      title: "Explore",
      items: [
        { label: "How I Help", href: "/#improve" },
        { label: "Growth Diagnostic", href: "/diagnostic" },
        { label: "Examples", href: "/#examples" },
        { label: "Results", href: "/#results" },
        { label: "About", href: "/about" },
      ],
    },
    {
      title: "Connect",
      items: [
        { label: "Book a Conversation", href: contactInfo.bookingUrl, external: true },
        { label: "WhatsApp", href: contactInfo.whatsapp, external: true },
        { label: "LinkedIn", href: contactInfo.linkedin, external: true },
        { label: "hello@teqie.co.uk", href: "mailto:hello@teqie.co.uk", external: true },
        { label: "Resiax ↗", href: "https://resiax.com", external: true },
      ],
    },
  ],
  social: [
    { icon: Linkedin, href: contactInfo.linkedin, label: "LinkedIn" },
    { icon: MessageSquare, href: contactInfo.whatsapp, label: "WhatsApp" },
    { icon: Mail, href: "mailto:hello@teqie.co.uk", label: "Email" },
  ],
  copyright: `© ${new Date().getFullYear()} Teqie Ltd. All rights reserved.`,
  location: "United Kingdom",
};
